import type { ColorGroup, IconKey, Tile } from './types'

/** ข้อมูลกลุ่มสีของโฉนด */
export const GROUPS: Record<ColorGroup, { name: string; color: string; ink: string }> = {
  slum: { name: 'ย่านเก่านีออน', color: '#8d6e4a', ink: '#fff' },
  edu: { name: 'เขตเรียนรู้', color: '#67d1f5', ink: '#053244' },
  service: { name: 'ย่านบริการ', color: '#f183c4', ink: '#4a0f33' },
  industry: { name: 'นิคมหุ่นยนต์', color: '#f89b4b', ink: '#452000' },
  retail: { name: 'ย่านค้าปลีก', color: '#f4595f', ink: '#fff' },
  office: { name: 'เขตสำนักงาน', color: '#f5d34b', ink: '#4a3a00' },
  tech: { name: 'ซิลิคอนโดม', color: '#5fd39a', ink: '#053a26' },
  orbit: { name: 'เขตวงโคจร', color: '#8b7bf0', ink: '#fff' },
}

const money = (n: number, step = 100) => Math.round(n / step) * step

/** สร้างช่องโฉนด: ค่าเช่าและกระแสเงินสดต่อเดือนคำนวณจากราคา เพื่อให้บาลานซ์ทั้งกระดาน */
function prop(
  id: number,
  name: string,
  emoji: string,
  icon: IconKey,
  group: ColorGroup,
  price: number,
  note?: string,
): Tile {
  const base = money(price * 0.1)
  return {
    id,
    kind: 'property',
    name,
    emoji,
    icon,
    group,
    price,
    rent: [base, base * 5, base * 14, base * 21, base * 27],
    upgradeCost: money(price * 0.5, 1000),
    // สัมประสิทธิ์นี้คุมความยาวเกม: ยิ่งสูง Passive Income ยิ่งไล่ทันรายจ่ายเร็ว
    monthly: [0, 1, 2, 3, 4].map((lv) => money(price * (0.024 + 0.016 * lv), 50)),
    note,
  }
}

function transport(id: number, name: string, emoji: string, icon: IconKey): Tile {
  return {
    id,
    kind: 'transport',
    name,
    emoji,
    icon,
    price: 200_000,
    // index = จำนวนท่าขนส่งที่เจ้าของถือ - 1
    rent: [10_000, 22_000, 46_000, 95_000],
    monthly: [5_000],
    note: 'ยิ่งถือหลายท่า ค่าผ่านทางยิ่งพุ่ง',
  }
}

function utility(id: number, name: string, emoji: string, icon: IconKey): Tile {
  return {
    id,
    kind: 'utility',
    name,
    emoji,
    icon,
    price: 150_000,
    // ตัวคูณแต้มเต๋า: ถือ 1 แห่ง = x4,000 / ถือ 2 แห่ง = x10,000
    rent: [4_000, 10_000],
    monthly: [4_000],
    note: 'ค่าเช่า = แต้มเต๋า x ตัวคูณ',
  }
}

export const BOARD: Tile[] = [
  { id: 0, kind: 'start', name: 'จุดเริ่มต้น', emoji: '🚀', icon: 'start', note: 'ผ่านช่องนี้ = รับ Payday' },
  prop(1, 'ตลาดนัดโฮโล', '🏮', 'market', 'slum', 60_000),
  { id: 2, kind: 'deal', name: 'โอกาสธุรกิจ', emoji: '💡', icon: 'deal', note: 'จั่วการ์ด DEAL' },
  prop(3, 'ร้านกาแฟหุ่นยนต์', '☕', 'coffee', 'slum', 60_000),
  {
    id: 4,
    kind: 'tax',
    name: 'ภาษีรายได้',
    emoji: '🧾',
    icon: 'tax',
    taxRate: 0.1,
    note: 'จ่าย 10% ของสินทรัพย์รวม',
  },
  transport(5, 'ท่าไฮเปอร์ลูป', '🚄', 'train'),
  prop(6, 'ติวเตอร์ AI', '🎓', 'school', 'edu', 100_000),
  { id: 7, kind: 'event', name: 'เหตุการณ์', emoji: '🛰️', icon: 'event', note: 'จั่วการ์ดเหตุการณ์' },
  prop(8, 'คลินิกโดรน', '🚑', 'clinic', 'edu', 100_000),
  prop(9, 'ยิมโฮโลแกรม', '🏋️', 'gym', 'edu', 120_000),
  {
    id: 10,
    kind: 'quarantine',
    name: 'สถานีกักกัน',
    emoji: '🧊',
    icon: 'quarantine',
    note: 'แวะเยี่ยมเฉย ๆ (ถ้าไม่ได้ถูกจับ)',
  },
  prop(11, 'อู่ซ่อมหุ่นยนต์', '🔧', 'garage', 'service', 140_000),
  utility(12, 'โรงไฟฟ้าฟิวชัน', '⚡', 'power'),
  prop(13, 'คาร์แคร์ไร้คนขับ', '🚗', 'carwash', 'service', 140_000),
  prop(14, 'สตูดิโอสตรีมมิ่ง', '🎬', 'studio', 'service', 160_000),
  transport(15, 'ท่าขนส่งวงโคจร', '🛸', 'orbit'),
  prop(16, 'ฟาร์มแนวตั้ง', '🌱', 'farm', 'industry', 180_000),
  { id: 17, kind: 'deal', name: 'โอกาสธุรกิจ', emoji: '💡', icon: 'deal', note: 'จั่วการ์ด DEAL' },
  prop(18, 'โรงพิมพ์ 3D', '🖨️', 'printer', 'industry', 180_000),
  prop(19, 'ศูนย์ข้อมูลคลาวด์', '🗄️', 'server', 'industry', 200_000),
  {
    id: 20,
    kind: 'jackpot',
    name: 'ท่าอวกาศพักผ่อน',
    emoji: '🏝️',
    icon: 'jackpot',
    note: 'เก็บกองกลางไปทั้งหมด',
  },
  prop(21, 'ห้างเมตาเวิร์ส', '🛍️', 'mall', 'retail', 220_000),
  { id: 22, kind: 'event', name: 'เหตุการณ์', emoji: '🛰️', icon: 'event', note: 'จั่วการ์ดเหตุการณ์' },
  prop(23, 'โรงพยาบาลนาโน', '🏥', 'hospital', 'retail', 220_000),
  prop(24, 'สวนสนุก VR', '🎡', 'park', 'retail', 240_000),
  transport(25, 'ท่าขนส่งใต้ทะเล', '🐋', 'sub'),
  prop(26, 'ตึกออฟฟิศอัจฉริยะ', '🏢', 'office', 'office', 260_000),
  prop(27, 'โรงแรมลอยฟ้า', '🏨', 'hotel', 'office', 260_000),
  utility(28, 'โรงบำบัดน้ำควอนตัม', '💧', 'water'),
  prop(29, 'คอนโดสมาร์ทซิตี้', '🏬', 'condo', 'office', 280_000),
  {
    id: 30,
    kind: 'gotoQuarantine',
    name: 'ตำรวจไซเบอร์จับ!',
    emoji: '🚨',
    icon: 'police',
    note: 'ไปสถานีกักกันทันที',
  },
  prop(31, 'เหมืองคริปโตดวงจันทร์', '🌙', 'mine', 'tech', 300_000),
  prop(32, 'โรงงานหุ่นยนต์', '🤖', 'robot', 'tech', 300_000),
  { id: 33, kind: 'deal', name: 'โอกาสธุรกิจ', emoji: '💡', icon: 'deal', note: 'จั่วการ์ด DEAL' },
  prop(34, 'แล็บชีวการแพทย์', '🧬', 'lab', 'tech', 320_000),
  transport(35, 'ท่าอวกาศนานาชาติ', '🌍', 'globe'),
  { id: 36, kind: 'event', name: 'เหตุการณ์', emoji: '🛰️', icon: 'event', note: 'จั่วการ์ดเหตุการณ์' },
  prop(37, 'ควอนตัมทาวเวอร์', '🗼', 'tower', 'orbit', 350_000),
  {
    id: 38,
    kind: 'tax',
    name: 'ภาษีความมั่งคั่ง',
    emoji: '💸',
    icon: 'tax',
    taxRate: 0.15,
    note: 'จ่าย 15% ของสินทรัพย์รวม',
  },
  prop(39, 'สถานีอวกาศออร์บิทัล', '🛰️', 'station', 'orbit', 400_000),
]

export const BOARD_SIZE = BOARD.length
export const QUARANTINE_TILE = 10
export const PAYDAY_TILE = 0

/** ระดับอัปเกรดสูงสุดของโฉนด */
export const MAX_LEVEL = 4

export const LEVEL_NAMES = [
  'ที่ดินเปล่า',
  'อัปเกรด 1 — โมดูลาร์',
  'อัปเกรด 2 — ไฮเทค',
  'อัปเกรด 3 — สมาร์ทฮับ',
  'อัปเกรด 4 — เมกะทาวเวอร์',
]

/** ช่องทั้งหมดในกลุ่มสีเดียวกัน */
export function groupTiles(group: ColorGroup): Tile[] {
  return BOARD.filter((t) => t.kind === 'property' && t.group === group)
}

export function tilesOfKind(kind: Tile['kind']): Tile[] {
  return BOARD.filter((t) => t.kind === kind)
}
